import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Crown,
    Users,
    Building2,
    Film,
    LayoutTemplate,
    Megaphone,
    CalendarDays,
    BadgeCheck,
    Sparkles,
    AlertCircle,
    CheckCircle,
    TrendingUp,
} from "lucide-react";
import api from "../../api/axios";

export default function SubscriptionComponent({ role }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [usage, setUsage] = useState(null);

    useEffect(() => {
        loadSubscription();
    }, []);

    const loadSubscription = async () => {
        try {
            setLoading(true);
            const response = await api.get("/subscription/my");
            if (response?.data?.success) {
                setSubscription(response.data.data.sub);
                setUsage(response.data.data.usage);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return "Unlimited";
        return new Date(date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    // Resource list with icons & colors
    const resources = [
        {
            key: "users",
            label: "Users",
            icon: Users,
            color: "blue",
        },
        {
            key: "brands",
            label: "Brands",
            icon: Building2,
            color: "green",
        },
        {
            key: "templates",
            label: "Templates",
            icon: LayoutTemplate,
            color: "purple",
        },
        {
            key: "creatives",
            label: "Creatives",
            icon: Film,
            color: "pink",
        },
        {
            key: "campaigns",
            label: "Campaigns",
            icon: Megaphone,
            color: "orange",
        },
    ];

    // Helper to get usage for a resource
    const getResourceUsage = (key) => {
        if (!usage || !usage.resources) return null;
        return usage.resources[key] || null;
    };

    // Helper to get color classes based on usage percentage
    const getUsageColor = (used, limit) => {
        if (limit === 0) return "gray";
        const percentage = (used / limit) * 100;
        if (percentage < 70) return "green";
        if (percentage < 90) return "yellow";
        return "red";
    };

    if (loading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
            </div>
        );
    }

    if (!subscription) {
        return (
            <div className="flex h-96 flex-col items-center justify-center rounded-3xl bg-white p-8 shadow">
                <AlertCircle className="h-12 w-12 text-gray-400" />
                <h2 className="mt-4 text-xl font-semibold text-gray-700">
                    No Subscription Found
                </h2>
                <p className="text-gray-500">
                    Please contact your administrator to set up a subscription.
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/50 p-6">
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Subscription</h1>
                    <p className="mt-1 text-gray-500">
                        Monitor your plan usage and manage your organization's resources.
                    </p>
                </div>

                {/* Main Plan Card */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 p-8 text-white shadow-2xl">
                    <div className="absolute right-0 top-0 -translate-y-1/2 translate-x-1/2 opacity-10">
                        <Crown size={200} />
                    </div>
                    <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <div className="mb-4 flex items-center gap-4">
                                <div className="rounded-2xl bg-white/20 p-3 backdrop-blur-sm">
                                    <Crown size={28} />
                                </div>
                                <div>
                                    <p className="text-sm uppercase tracking-widest text-blue-100">
                                        Current Plan
                                    </p>
                                    <h2 className="text-4xl font-extrabold">
                                        {subscription.sub_plan}
                                    </h2>
                                </div>
                            </div>
                            <p className="max-w-xl text-blue-100">
                                Your organization is on the{" "}
                                <strong>{subscription.sub_plan}</strong> plan. Upgrade to unlock
                                more resources and features.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-6 lg:grid-cols-2">
                            <div>
                                <p className="text-sm text-blue-100">Status</p>
                                <div className="mt-2 flex items-center gap-2">
                                    <BadgeCheck className="text-green-300" />
                                    <span className="font-semibold">
                                        {subscription.sub_status}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <p className="text-sm text-blue-100">Started</p>
                                <div className="mt-2 flex items-center gap-2">
                                    <CalendarDays size={18} />
                                    <span>{formatDate(subscription.sub_start_date)}</span>
                                </div>
                            </div>
                            {/* <div>
                                <p className="text-sm text-blue-100">Expires</p>
                                <p className="mt-2 font-semibold">
                                    {formatDate(subscription.sub_end_date)}
                                </p>
                            </div> */}
                        </div>

                        <button
                            onClick={() => navigate("/organization/plans")}
                            className="rounded-xl bg-white px-8 py-3 font-semibold text-indigo-700 shadow-lg transition hover:scale-105 hover:shadow-xl"
                        >
                            Upgrade Plan
                        </button>
                    </div>
                </div>

                {/* Resource Usage Section */}
                <div>
                    <div className="mb-5 flex items-center gap-2">
                        <TrendingUp className="text-indigo-500" size={24} />
                        <h2 className="text-2xl font-bold text-gray-800">
                            Resource Usage
                        </h2>
                        <span className="ml-auto text-sm text-gray-400">
                            {usage?.status || "ACTIVE"}
                        </span>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {resources.map((res) => {
                            const data = getResourceUsage(res.key);
                            if (!data) return null;
                            const { used, limit, remaining } = data;
                            const percentage = limit > 0 ? Math.round((used / limit) * 100) : 0;
                            const color = getUsageColor(used, limit);
                            const colorMap = {
                                green: "bg-green-500",
                                yellow: "bg-yellow-500",
                                red: "bg-red-500",
                                gray: "bg-gray-400",
                            };
                            const bgMap = {
                                green: "bg-green-100",
                                yellow: "bg-yellow-100",
                                red: "bg-red-100",
                                gray: "bg-gray-100",
                            };
                            const textMap = {
                                green: "text-green-600",
                                yellow: "text-yellow-600",
                                red: "text-red-600",
                                gray: "text-gray-600",
                            };

                            return (
                                <div
                                    key={res.key}
                                    className="rounded-2xl bg-white p-6 shadow transition hover:shadow-lg"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`rounded-xl ${bgMap[color]} p-2 ${textMap[color]}`}
                                            >
                                                <res.icon size={22} />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-700">{res.label}</p>
                                                <p className="text-sm text-gray-500">
                                                    {used} / {limit} used
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`text-sm font-bold ${textMap[color]}`}>
                                            {remaining} remaining
                                        </span>
                                    </div>

                                    <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                                        <div
                                            className={`h-full ${colorMap[color]} transition-all duration-500`}
                                            style={{ width: `${Math.min(percentage, 100)}%` }}
                                        />
                                    </div>

                                    <div className="mt-3 flex justify-between text-xs text-gray-400">
                                        <span>{percentage}% used</span>
                                        <span>{limit > 0 ? `${limit} total` : "Unlimited"}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Subscription Details (collapsible or always visible) */}
                <div className="rounded-2xl bg-white shadow">
                    <div className="border-b border-gray-100 p-6">
                        <h2 className="text-xl font-bold text-gray-800">
                            Subscription Details
                        </h2>
                    </div>
                    <div className="grid gap-6 p-6 md:grid-cols-2 lg:grid-cols-3">
                        <div>
                            <label className="text-sm text-gray-500">Subscription ID</label>
                            <p className="mt-1 break-all font-medium">
                                {subscription.sub_id}
                            </p>
                        </div>
                        <div>
                            <label className="text-sm text-gray-500">Plan</label>
                            <p className="mt-1 font-medium">{subscription.sub_plan}</p>
                        </div>
                        <div>
                            <label className="text-sm text-gray-500">Status</label>
                            <p className="mt-1 font-medium text-green-600">
                                {subscription.sub_status}
                            </p>
                        </div>
                        <div>
                            <label className="text-sm text-gray-500">Start Date</label>
                            <p className="mt-1 font-medium">
                                {formatDate(subscription.sub_start_date)}
                            </p>
                        </div>
                        <div>
                            <label className="text-sm text-gray-500">Expiry</label>
                            <p className="mt-1 font-medium">
                                {formatDate(subscription.sub_end_date)}
                            </p>
                        </div>
                        <div>
                            <label className="text-sm text-gray-500">Notes</label>
                            <p className="mt-1 font-medium">
                                {subscription.sub_notes || "—"}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}